import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Select,
  Modal,
  Input,
  InputNumber,
  message,
  Alert,
  Tag,
  Popconfirm,
} from 'antd';
import { PlusOutlined, LinkOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import intercompanyService, { type AffiliateCompany } from '@/services/intercompany.service';

const { Title, Paragraph } = Typography;

const IntercompanyAffiliatesPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<AffiliateCompany | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>();
  const [notes, setNotes] = useState('');
  const [vatRate, setVatRate] = useState<number>(5);
  const [editNotes, setEditNotes] = useState('');
  const [editVat, setEditVat] = useState<number>(0);

  const { data: affiliates = [], isLoading } = useQuery({
    queryKey: ['intercompany-affiliates'],
    queryFn: () => intercompanyService.listAffiliates(),
  });

  const {
    data: tenantCompanies = [],
    isLoading: loadingCompanies,
    isError: companiesError,
    error: companiesErr,
    refetch: refetchCompanies,
  } = useQuery({
    queryKey: ['intercompany-tenant-companies'],
    queryFn: () => intercompanyService.listTenantCompanies(),
    enabled: modalOpen,
  });

  const createMut = useMutation({
    mutationFn: () => intercompanyService.createAffiliate(selectedCompanyId!, notes || undefined),
    onSuccess: async (link: { id?: string }) => {
      if (link?.id && vatRate !== 0) {
        try {
          await intercompanyService.updateAffiliate(link.id, { vat_rate: vatRate });
        } catch {
          /* link created; VAT can be set later */
        }
      }
      message.success(t('affiliateLinkCreated'));
      setModalOpen(false);
      setSelectedCompanyId(undefined);
      setNotes('');
      setVatRate(5);
      qc.invalidateQueries({ queryKey: ['intercompany-affiliates'] });
      qc.invalidateQueries({ queryKey: ['intercompany-tenant-companies'] });
    },
    onError: (e: Error) => message.error(e.message || t('failedToCreateLink')),
  });

  const updateMut = useMutation({
    mutationFn: () =>
      intercompanyService.updateAffiliate(editing!.link_id, {
        vat_rate: editVat,
        notes: editNotes || null,
      }),
    onSuccess: () => {
      message.success(t('affiliateUpdated'));
      setEditOpen(false);
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['intercompany-affiliates'] });
    },
    onError: (e: Error) => message.error(e.message || t('updateFailed')),
  });

  const deactivateMut = useMutation({
    mutationFn: (linkId: string) => intercompanyService.updateAffiliate(linkId, { is_active: false }),
    onSuccess: () => {
      message.success(t('affiliateDeactivated'));
      qc.invalidateQueries({ queryKey: ['intercompany-affiliates'] });
    },
    onError: (e: Error) => message.error(e.message || t('failedGeneric')),
  });

  const linkedIds = new Set(affiliates.map((a) => a.company_id));
  const availableCompanies = tenantCompanies.filter((c) => !linkedIds.has(c.id));

  const openEdit = (row: AffiliateCompany) => {
    setEditing(row);
    setEditVat(Number(row.vat_rate ?? 0));
    setEditNotes(row.notes || '');
    setEditOpen(true);
  };

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {t('intercompanyAffiliates')}
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('intercompanyAffiliatesSubtitle')}
          </Paragraph>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
          {t('linkAffiliateCompany')}
        </Button>
      </Space>

      <Card>
        <Table
          loading={isLoading}
          rowKey="link_id"
          dataSource={affiliates}
          columns={[
            { title: t('companyCode'), dataIndex: 'company_code' },
            { title: t('companyName'), dataIndex: 'company_name' },
            {
              title: t('vatPercent'),
              dataIndex: 'vat_rate',
              width: 90,
              render: (v) => `${Number(v ?? 0)}%`,
            },
            {
              title: tc('status'),
              key: 'status',
              render: () => <Tag color="success">{t('active')}</Tag>,
            },
            {
              title: tc('actions'),
              key: 'actions',
              width: 280,
              render: (_: unknown, r: AffiliateCompany) => (
                <Space wrap>
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
                    {tc('edit')}
                  </Button>
                  <Button
                    size="small"
                    onClick={() =>
                      navigate(`/fin/reports/intercompany-statement?affiliate=${r.company_id}`)
                    }
                  >
                    {t('statement')}
                  </Button>
                  <Popconfirm
                    title={t('deactivateAffiliateLinkConfirm')}
                    onConfirm={() => deactivateMut.mutate(r.link_id)}
                  >
                    <Button size="small" danger>
                      {t('deactivate')}
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
          locale={{ emptyText: t('noAffiliateCompaniesLinkedYet') }}
        />
      </Card>

      <Modal
        title={t('linkAffiliateCompany')}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => {
          if (!selectedCompanyId) {
            message.warning(t('selectACompany'));
            return;
          }
          createMut.mutate();
        }}
        confirmLoading={createMut.isPending}
        okText={t('createLink')}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {companiesError && (
            <Alert
              type="error"
              showIcon
              message={companiesErr instanceof Error ? companiesErr.message : t('failedToLoadCompanies')}
              action={
                <Button size="small" onClick={() => refetchCompanies()}>
                  {t('retry')}
                </Button>
              }
            />
          )}
          <div>
            <Typography.Text strong>{t('affiliateCompany')}</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              placeholder={
                loadingCompanies
                  ? t('loadingCompanies')
                  : availableCompanies.length
                    ? t('selectCompany')
                    : t('noOtherCompaniesAvailable')
              }
              loading={loadingCompanies}
              value={selectedCompanyId}
              onChange={setSelectedCompanyId}
              options={availableCompanies.map((c) => ({
                value: c.id,
                label: `${c.code} — ${c.name}${c.same_tenant === false ? ` ${t('otherTenantWillJoin')}` : ''}`,
              }))}
              showSearch
              optionFilterProp="label"
            />
          </div>
          <div>
            <Typography.Text strong>{t('defaultVatPercent')}</Typography.Text>
            <InputNumber
              style={{ width: '100%', marginTop: 8 }}
              min={0}
              max={100}
              value={vatRate}
              onChange={(v) => setVatRate(Number(v ?? 0))}
            />
          </div>
          <div>
            <Typography.Text strong>{t('notesOptional')}</Typography.Text>
            <Input.TextArea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} style={{ marginTop: 8 }} />
          </div>
          <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
            <LinkOutlined /> {t('affiliateLinkSeedsHint')}
          </Paragraph>
        </Space>
      </Modal>

      <Modal
        title={editing ? t('editNamed', { name: editing.company_name }) : t('editAffiliate')}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={() => updateMut.mutate()}
        confirmLoading={updateMut.isPending}
        okText={tc('save')}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Typography.Text strong>{t('vatPercent')}</Typography.Text>
            <InputNumber
              style={{ width: '100%', marginTop: 8 }}
              min={0}
              max={100}
              value={editVat}
              onChange={(v) => setEditVat(Number(v ?? 0))}
            />
          </div>
          <div>
            <Typography.Text strong>{t('notes')}</Typography.Text>
            <Input.TextArea
              rows={2}
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              style={{ marginTop: 8 }}
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default IntercompanyAffiliatesPage;
